/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {
  ComponentRef,
  EffectRef,
  inject,
  Injector,
  Resource,
  resourceFromSnapshots,
  Signal,
  signal,
  DestroyRef,
  ResourceSnapshot,
  effect,
  computed,
  assertInInjectionContext,
  reflectComponentType,
} from '@angular/core';
import {Router} from './router';
import type {ActivatedRoute} from './router_state';
import {
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  NavigationSkipped,
  NavigationCancellationCode,
} from './events';

export const BLOCKING_SYMBOL: unique symbol = /* @__PURE__ */ Symbol(
  typeof ngDevMode === 'undefined' || ngDevMode ? '__isBlocking' : '',
);
export const SOURCE_RESOURCE_SYMBOL: unique symbol = /* @__PURE__ */ Symbol(
  typeof ngDevMode === 'undefined' || ngDevMode ? '__sourceResource' : '',
);

/**
 * @internal
 */
export interface InternalRouterResource<T = unknown> extends Resource<T> {
  [SOURCE_RESOURCE_SYMBOL]: Resource<T>;
  [BLOCKING_SYMBOL]?: boolean;
  reload(): boolean;
}

/**
 * Marks a resource as non-blocking. The Router will NOT wait for this resource to resolve
 * before completing the navigation.
 * @developerPreview 22.2
 */
export function nonBlocking<T, R extends Resource<T>>(res: R): R {
  (res as unknown as InternalRouterResource<T>)[BLOCKING_SYMBOL] = false;
  return res;
}

/**
 * Wraps a Resource to make it cooperative with the Angular Router, freezing its state
 * during navigation transitions and handling rollback recovery.
 */
export function routerResource<T>(source: Resource<T>): Resource<T> & {reload(): boolean} {
  ngDevMode && assertInInjectionContext(routerResource);
  const injector = inject(Injector);
  const router = injector.get(Router);

  const {snapshot: snapshotSignal, frozenSnapshot} = createTransactionalSnapshot(
    source,
    router,
    injector,
  );

  const res = resourceFromSnapshots(snapshotSignal) as unknown as InternalRouterResource<T>;

  res[SOURCE_RESOURCE_SYMBOL] = source;
  res[BLOCKING_SYMBOL] =
    (source as unknown as InternalRouterResource<T>)[BLOCKING_SYMBOL] !== false;

  res.reload = function (): boolean {
    // If the resource is currently frozen (e.g., during an active navigation transition
    // or while recovering from a cancelled navigation), we reject manual reload requests.
    // Triggering a reload during an active navigation (where the resource may already be
    // reactively loading new parameters behind the scenes) would disrupt the router's resource
    // tracking for the transition. Similarly, during a rollback recovery, the router is
    // already managing the resource reload to restore the previous state.
    if (frozenSnapshot() !== null) {
      return false;
    }
    return (source as any).reload?.() ?? false;
  };

  return res;
}

/**
 * Creates a signal that tracks the resource snapshot and handles transactional behavior
 * (freezing during navigation and rollback recovery).
 */
function createTransactionalSnapshot<T>(
  source: Resource<T>,
  router: Router,
  injector: Injector,
): {
  snapshot: Signal<ResourceSnapshot<T>>;
  frozenSnapshot: Signal<ResourceSnapshot<T> | null>;
} {
  // Holds a snapshot of the resource to keep the UI masked (frozen) during pending navigations
  // or while recovering from a cancelled navigation.
  const frozenSnapshot = signal<ResourceSnapshot<T> | null>(null);

  // Tracks whether we are in a recovery phase after a cancelled navigation.
  // The intended behavior is that on cancellation, the router reverts to the previous state.
  // This reversion might trigger a new load of the previous state because the signal dependencies
  // changed. If we were to release the frozen resource state immediately, the user would see a loading state
  // for data they were just looking at. To avoid this "loading flash", we retain the frozen
  // value (via frozenSnapshot) during this recovery load/reload until the resource settles.
  const isRollbackRecoveryPending = signal(false);

  const sub = router.events.subscribe((e) => {
    if (e instanceof NavigationStart) {
      isRollbackRecoveryPending.set(false);

      if (frozenSnapshot() === null) {
        // Freeze the snapshot at the start of navigation to keep the UI stable.
        frozenSnapshot.set(source.snapshot());
      }
    } else if (e instanceof NavigationEnd) {
      // Navigation succeeded, so we can unfreeze and use the live state.
      frozenSnapshot.set(null);
      isRollbackRecoveryPending.set(false);
    } else if (e instanceof NavigationSkipped) {
      // If a navigation is skipped while we have a frozen snapshot (e.g. navigating to the
      // current URL to cancel an in-flight navigation), the in-flight navigation is aborted
      // and parameter rollback begins. We must maintain the frozen snapshot until the rollback
      // recovery load completes to prevent flashing a loading state.
      if (frozenSnapshot() !== null) {
        isRollbackRecoveryPending.set(true);
      }
    } else if (e instanceof NavigationCancel || e instanceof NavigationError) {
      const isRollback =
        e instanceof NavigationError ||
        (e instanceof NavigationCancel &&
          e.code !== NavigationCancellationCode.SupersededByNewNavigation &&
          e.code !== NavigationCancellationCode.Redirect);

      if (!isRollback) return;

      isRollbackRecoveryPending.set(true);
    }
  });

  injector.get(DestroyRef).onDestroy(() => sub.unsubscribe());

  effect(
    () => {
      if (isRollbackRecoveryPending() && !source.isLoading()) {
        isRollbackRecoveryPending.set(false);
        frozenSnapshot.set(null);
      }
    },
    {injector},
  );

  return {
    snapshot: computed(() => frozenSnapshot() ?? source.snapshot()),
    frozenSnapshot,
  };
}

/**
 * Creates reactive effects to bind the unwrapped values of blocking router resources
 * to matching component inputs.
 *
 * Non-blocking resources are bound as the `Resource` instance itself through the standard
 * route data stream in `RoutedComponentInputBinder`. In contrast, blocking resources have
 * resolved before navigation completes and bind their unwrapped `.value()` to the component
 * input. This sets up an `effect` for each matching blocking resource to keep the component
 * input reactively updated, and returns the handled keys so the standard data subscription
 * skips them.
 */
export function createResourceOutletBindingEffects(
  componentRef: ComponentRef<unknown>,
  route: ActivatedRoute,
): {createdEffects: EffectRef[]; handledKeys: string[]} {
  const createdEffects: EffectRef[] = [];
  const handledKeys: string[] = [];
  const mirror = route.component ? reflectComponentType(route.component) : null;
  const resources = route.resources;
  if (!mirror || !resources) {
    return {createdEffects, handledKeys};
  }

  for (const {templateName} of mirror.inputs) {
    const resource = resources[templateName];
    if (!resource || !(resource as InternalRouterResource)[BLOCKING_SYMBOL]) {
      continue;
    }

    const effectRef = effect(
      () => {
        componentRef.setInput(templateName, resource.value());
      },
      {injector: componentRef.injector},
    );
    createdEffects.push(effectRef);
    handledKeys.push(templateName);
  }

  return {createdEffects, handledKeys};
}
